/**
 * WhatsNewCard — bottom-right floating card surfacing unseen release notes.
 *
 * Behavior (driven by parent):
 *   - Parent decides whether to render this at all (presence of unseen entries
 *     + not a fresh-install bootstrap)
 *   - "Got it" → onDismiss(currentVersion), writes localStorage seen marker
 *   - "View all past changes" → onShowAll(), opens WhatsNewModal
 *
 * Design choices:
 *   - Floating bottom-right (matches saveToast position so the user knows
 *     where to look). Slide-in animation reuses existing animate-slide-up.
 *   - Purple gradient header — distinct from amber (counter-bias) and
 *     emerald (live pattern stats) so the user can identify at a glance.
 *   - Max 3 release entries rendered inline; if more, show "+N older" link
 *     that routes to onShowAll. Avoids a wall of text when a user opens
 *     after a multi-version absence.
 *   - Each highlight gets a colored dot per severity (feature/fix/etc).
 *     Tooltip on the dot shows the severity label.
 *
 * Always-dismissible. Never blocks UI.
 */

import { Sparkles, X } from 'lucide-react';
import { SEVERITY_COLOR, CURRENT_VERSION } from '../../utils/whatsNew';

const MAX_INLINE_ENTRIES = 3;

const SeverityDot = ({ severity = 'feature' }) => {
  const token = SEVERITY_COLOR[severity] || SEVERITY_COLOR.feature;
  return (
    <span
      className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 mr-2 shrink-0 ${token.dot}`}
      title={token.label}
      aria-label={token.label}
    />
  );
};

const HighlightRow = ({ highlight, darkMode }) => (
  <li className="flex items-start gap-0">
    <SeverityDot severity={highlight.severity} />
    <div className="flex-1 min-w-0">
      <p className={`text-[11px] leading-snug ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
        {highlight.text}
      </p>
      {highlight.action && (
        <p className={`text-[10px] italic mt-0.5 leading-snug ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          → {highlight.action}
        </p>
      )}
    </div>
  </li>
);

const EntryBlock = ({ entry, darkMode, showHeader }) => (
  <div className="space-y-1.5">
    {showHeader && (
      <div className="flex items-baseline gap-2">
        <span className={`text-[11px] font-semibold ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
          v{entry.version}
        </span>
        <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          {entry.date}
        </span>
        {entry.title && (
          <span className={`text-[10px] italic ${darkMode ? 'text-gray-400' : 'text-gray-600'} truncate`}>
            — {entry.title}
          </span>
        )}
      </div>
    )}
    <ul className="space-y-1">
      {(entry.highlights || []).map((h, i) => (
        <HighlightRow key={i} highlight={h} darkMode={darkMode} />
      ))}
    </ul>
  </div>
);

const WhatsNewCard = ({ entries, onDismiss, onShowAll, darkMode = true }) => {
  if (!Array.isArray(entries) || entries.length === 0) return null;

  const inlineEntries = entries.slice(0, MAX_INLINE_ENTRIES);
  const overflowCount = Math.max(0, entries.length - MAX_INLINE_ENTRIES);
  const showVersionHeaders = inlineEntries.length > 1;

  return (
    <div className="fixed bottom-6 right-6 z-100 animate-slide-up max-w-md w-[26rem]">
      <div className={`relative rounded-xl shadow-2xl border backdrop-blur-md overflow-hidden ${
        darkMode
          ? 'bg-gradient-to-br from-purple-950/95 via-gray-900/95 to-gray-900/95 border-purple-500/40'
          : 'bg-gradient-to-br from-purple-50 via-white to-white border-purple-300'
      }`}>
        {/* Header strip — purple gradient title */}
        <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${
          darkMode ? 'border-purple-500/20 bg-purple-500/10' : 'border-purple-200 bg-purple-50/60'
        }`}>
          <div className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${
            darkMode ? 'bg-purple-500/30 text-purple-200' : 'bg-purple-200 text-purple-700'
          }`}>
            <Sparkles size={12} />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-[10px] uppercase tracking-wider font-semibold ${
              darkMode ? 'text-purple-200' : 'text-purple-700'
            }`}>
              What&apos;s new in v{entries[0].version}
            </p>
            {!showVersionHeaders && entries[0].title && (
              <p className={`text-[11px] leading-tight ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {entries[0].title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss what's new"
            title="Got it"
            className={`p-1 rounded-md transition-colors ${
              darkMode ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body — highlights */}
        <div className="px-4 py-3 space-y-3 max-h-[28rem] overflow-y-auto">
          {inlineEntries.map((entry) => (
            <EntryBlock
              key={entry.version}
              entry={entry}
              darkMode={darkMode}
              showHeader={showVersionHeaders}
            />
          ))}
          {overflowCount > 0 && (
            <p className={`text-[10px] italic text-center pt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              + {overflowCount} older release{overflowCount === 1 ? '' : 's'} not shown
            </p>
          )}
        </div>

        {/* Footer — primary + secondary actions */}
        <div className={`flex items-center gap-2 px-4 py-2.5 border-t ${
          darkMode ? 'border-purple-500/15 bg-black/20' : 'border-purple-200 bg-purple-50/40'
        }`}>
          {typeof onShowAll === 'function' && (
            <button
              type="button"
              onClick={onShowAll}
              className={`text-[11px] px-2 py-1 rounded-md transition-colors ${
                darkMode
                  ? 'text-purple-300 hover:text-purple-100 hover:bg-purple-500/10'
                  : 'text-purple-700 hover:text-purple-900 hover:bg-purple-100'
              }`}
            >
              View all past changes
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onDismiss}
            className={`text-[11px] font-semibold px-3 py-1 rounded-md border transition-[background-color,border-color,box-shadow,color] duration-150 ${
              darkMode
                ? 'bg-purple-500/25 border-purple-400/50 text-purple-50 ring-1 ring-inset ring-purple-400/25 hover:bg-purple-500/35 hover:border-purple-400/70 hover:shadow-[0_0_14px_rgba(168,85,247,0.25)]'
                : 'bg-purple-100 border-purple-300 text-purple-800 hover:bg-purple-200 hover:border-purple-400'
            }`}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewCard;
export { CURRENT_VERSION };
